import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import AssetStatusCell from 'src/components/table/cell-types/AssetStatusCell';
import GpmCell from 'src/components/table/cell-types/GpmCell';
import ImportantTextCell from 'src/components/table/cell-types/ImportantTextCell';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {HydrantsOrder} from './__generated__/HydrantsPaginationQuery.graphql';
import {HydrantsTable_hydrants$key} from './__generated__/HydrantsTable_hydrants.graphql';
import {HydrantsTableDeleteHydrantMutation} from './__generated__/HydrantsTableDeleteHydrantMutation.graphql';
import {listPagehydrantsQuery} from './__generated__/listPagehydrantsQuery.graphql';
import TableFilters from './TableFilters';

type Props = {
  hydrants: HydrantsTable_hydrants$key;
};

type Filters = {
  text?: string;
  orderBy?: HydrantsOrder;
};

function HydrantsTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPagehydrantsQuery,
    HydrantsTable_hydrants$key
  >(
    graphql`
      fragment HydrantsTable_hydrants on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          text: {type: "String"}
          orderBy: {type: "HydrantsOrder"}
        )
        @refetchable(queryName: "HydrantsPaginationQuery") {
        hydrants(first: $first, after: $after, orderBy: $orderBy, text: $text)
          @connection(key: "HydrantsList_hydrants") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              serial
              hydrantGallonsPerMinute
              externalId
              status
              model {
                id
                name
              }
              family {
                id
                name
              }
              manufacturer {
                id
                name
              }
            }
          }
        }
      }
    `,
    props.hydrants,
  );

  const [commit] = useMutation<HydrantsTableDeleteHydrantMutation>(deleteHydrantMutation);
  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      isTransitioning={isTransitioning}
      actions={{show: true, delete: true, edit: true, create: true}}
      totalDataLength={data.hydrants.totalCount}
      columns={columns}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      data={data.hydrants.edges}
    />
  );

  function handleDelete(id: string) {
    commit({
      variables: {input: {hydrantId: id}},
      onCompleted: payload => {
        const entry = payload.deleteHydrant?.hydrant;

        if (entry) {
          showEventNotification('removed', 'Hydrant', entry.serial);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(root, 'HydrantsList_hydrants');

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({text: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }
}

export default HydrantsTable;

const deleteHydrantMutation = graphql`
  mutation HydrantsTableDeleteHydrantMutation($input: DeleteHydrantInput!) {
    deleteHydrant(input: $input) {
      hydrant {
        id
        serial
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'ID',
    accessor: 'node.externalId',
    id: 'external_id',
    Cell({cell: {value}}: any) {
      return <ImportantTextCell value={value} />;
    },
  },
  {
    Header: 'Serial',
    accessor: 'node.serial',
    id: 'serial',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Status',
    accessor: 'node.status',
    id: 'status',
    Cell({cell: {value}}: any) {
      return <AssetStatusCell value={value} />;
    },
  },

  {
    Header: 'Manufacturer',
    accessor: 'node.manufacturer.name',
    id: 'manufacturer_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Type',
    accessor: 'node.family.name',
    id: 'family_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Model',
    accessor: 'node.model.name',
    id: 'model_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'GPM',
    accessor: 'node.hydrantGallonsPerMinute',
    id: 'hydrant_gallons_per_minute',
    Cell({cell: {value}}: any) {
      return <GpmCell value={value} />;
    },
  },
];
